import { useState, useEffect, useRef } from "react";
import axios from "axios";
import "react-loading-skeleton/dist/skeleton.css";
import { toast, ToastContainer } from "react-toastify";
import useAppointments from "../../../hook/useAppointments";
import usePrintAppointment from "./AppointmentPrintService";
import AppointmentEditForm from "./AppointmentEditForm";
import AppointmentTable from "./AppointmentTable";
import PageHeading from "../../PageHeading";
import ConfirmActionModal from "../../ConfirmDeleteModal";
import AppointmentFiltersAndSummary from "../../AppointmentFiltersAndSummary";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const UpComing = () => {
  const { error, fetchUpcomingAppointments, approveAppointment } = useAppointments();
  const { handlePrint, PrintFrame } = usePrintAppointment();

  // Local state
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [editMode, setEditMode] = useState(null);
  const [editData, setEditData] = useState({
    doctorName: "",
    patientName: "",
    mobileNumber: "",
    serialNumber: "",
    weight: "",
    age: "",
    bloodGroup: "",
    consultationFee: "",
    paymentMethod: "",
    reason: "",
    address: "",
    appointmentDate: "",
    status: "",
  });
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [patientSearch, setPatientSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalTargetId, setApprovalTargetId] = useState(null);
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelTargetId, setCancelTargetId] = useState(null);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteIds, setBulkDeleteIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [printingAppointment, setPrintingAppointment] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewAppointment, setViewAppointment] = useState(null);
  const dropdownTriggerRefs = useRef({});
  const appointmentsPerPage = 10;

  // Fetch Upcoming Appointments on mount
  useEffect(() => {
    const loadUpcomingAppointments = async () => {
      try {
        const upcomingData = await fetchUpcomingAppointments();
        setTodayAppointments(upcomingData);
        setFilteredAppointments(upcomingData);
      } catch (err) {
        console.error("Error fetching upcoming appointments:", err);
      }
    };
    loadUpcomingAppointments();
  }, [fetchUpcomingAppointments]);

  // Apply filters whenever dependencies change
  useEffect(() => {
    if (!todayAppointments || !Array.isArray(todayAppointments)) return;

    let filtered = [...todayAppointments];

    // Filter out appointments on or before today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    filtered = filtered.filter((app) => {
      const appDate = new Date(app.appointmentDate || app.createdAt);
      appDate.setHours(0, 0, 0, 0);
      return appDate > today;
    });

    if (filterStatus === "complete") {
      filtered = filtered.filter((app) => app.status === "approved");
    } else if (filterStatus === "pending") {
      filtered = filtered.filter((app) => app.status === "pending");
    } else if (filterStatus === "cancelled") {
      filtered = filtered.filter((app) => app.status === "cancelled");
    }

    if (selectedDoctor) {
      filtered = filtered.filter((app) => app.doctorId === selectedDoctor);
    }

    if (patientSearch.trim()) {
      const search = patientSearch.toLowerCase();
      filtered = filtered.filter(
        (app) =>
          (app.patientName && app.patientName.toLowerCase().includes(search)) ||
          (app.mobileNumber && app.mobileNumber.includes(search))
      );
    }

    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter((app) => {
        const appDate = new Date(app.appointmentDate || app.createdAt);
        return appDate >= start;
      });
    }
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter((app) => {
        const appDate = new Date(app.appointmentDate || app.createdAt);
        return appDate <= end;
      });
    }

    setFilteredAppointments(filtered);
    setCurrentPage(1);
    // console.log("Today Appointments:", todayAppointments);
    // console.log("Filtered Appointments:", filtered);
  }, [
    todayAppointments,
    filterStatus,
    selectedDoctor,
    patientSearch,
    startDate,
    endDate,
  ]);

  // Utility functions
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return date.toLocaleDateString("en-US", { dateStyle: "medium" });
  };

  const getStatusLabel = (appointment) => {
    if (appointment.status === "cancelled") return "Cancelled";
    if (appointment.status === "approved") return "Complete";
    return "Pending";
  };

  // Handlers
  const initiatePrint = (appointment) => {
    setPrintingAppointment(appointment);
    handlePrint(appointment);
  };

  const handleApproveClick = (id) => {
    setApprovalTargetId(id);
    setApprovalModalVisible(true);
  };

  const confirmApproval = async (id) => {
    try {
      await approveAppointment(id);
      toast.success("Appointment approved successfully!");
      const upcomingData = await fetchUpcomingAppointments();
      setTodayAppointments(upcomingData);
      setFilteredAppointments(upcomingData);
    } catch (error) {
      console.error("Error approving appointment:", error);
      toast.error(`Error: ${error.message || "Failed to approve appointment"}`);
    }
  };

  const handleCancelClick = (id) => {
    setCancelTargetId(id);
    setCancelModalVisible(true);
  };

  const confirmCancellation = async (id) => {
    try {
      await axios.put(
        `${BASE_URL}/appointment/cancel/${id}`,
        {},
        { headers: { "x-api-key": API_KEY } }
      );
      toast.success("Appointment cancelled successfully!");
      const upcomingData = await fetchUpcomingAppointments();
      setTodayAppointments(upcomingData);
      setFilteredAppointments(upcomingData);
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      toast.error(`Error: ${error.message || "Failed to cancel appointment"}`);
    }
  };

  const handleEditStart = (appointment) => {
    let formattedBloodGroup = appointment.bloodGroup || "";
    formattedBloodGroup = formattedBloodGroup
      .replace("_POSITIVE", "+")
      .replace("_NEGATIVE", "-");

    setEditData({
      doctorName: appointment.doctorName || "",
      patientName: appointment.patientName || "",
      mobileNumber: appointment.mobileNumber || "",
      serialNumber: appointment.serialNumber || "",
      weight: appointment.weight || "",
      age: appointment.age || "",
      bloodGroup: formattedBloodGroup,
      consultationFee: appointment.consultationFee || "",
      paymentMethod: appointment.paymentMethod || "",
      reason: appointment.reason || "",
      address: appointment.address || "",
    });
    setEditMode(appointment.id);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleSaveEdit = async (id) => {
    try {
      const payload = { ...editData };
      const response = await axios.put(
        `${BASE_URL}/appointment/edit/${id}`,
        payload,
        { headers: { "x-api-key": API_KEY, "Content-Type": "application/json" } }
      );
      if (response.data && response.data.appointment) {
        toast.success("Appointment updated successfully");
        const upcomingData = await fetchUpcomingAppointments();
        setTodayAppointments(upcomingData);
        setFilteredAppointments(upcomingData);
        setEditMode(null);
      } else {
        throw new Error("Failed to update appointment");
      }
    } catch (error) {
      console.error("Error updating appointment:", error);
      toast.error(`Error: ${error.message || "Failed to update appointment"}`);
    }
  };

  const confirmBulkDelete = async (ids) => {
    try {
      setDeleting(true);
      await Promise.all(
        ids.map((id) =>
          axios.delete(`${BASE_URL}/appointment/delete/${id}`, {
            headers: { "x-api-key": API_KEY },
          })
        )
      );

      toast.success("Selected appointments deleted successfully!");
      setFilteredAppointments((prevAppointments) =>
        prevAppointments.filter((app) => !ids.includes(app.id))
      );

      setBulkDeleteIds([]);
    } catch (error) {
      toast.error("Bulk deletion failed.");
    } finally {
      setDeleting(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleReset = () => {
    setFilterStatus("all");
    setSelectedDoctor("");
    setPatientSearch("");
    setStartDate("");
    setEndDate("");
    setFilteredAppointments(todayAppointments);
    setCurrentPage(1);
  };

  // Dropdown handlers
  const toggleDropdown = (id) => {
    setActiveDropdown(activeDropdown === id ? null : id);
  };

  // View handler
  const handleView = (appointment) => {
    setViewAppointment(appointment);
    setViewModalOpen(true);
    setActiveDropdown(null);
  };

  if (error) {
    return <div className="text-red-500 p-4">Error loading appointments: {error.message}</div>;
  }

  const breadcrumbs = [
    { label: "Dashboard", url: "/dashboard" },
    { label: "Manage Appointments"},
    { label: "Upcoming Appointments" },
  ];

  // Doctor options for DynamicSelect
  const doctorOptions = todayAppointments.map((app) => ({
    value: app.doctorId,
    label: app.doctorName,
  }));

  return (
    <div>
      <PrintFrame appointment={printingAppointment} />
      <ToastContainer />
      <PageHeading title="Upcoming Appointments" breadcrumbs={breadcrumbs} />

      {/* Reusable Filters and Summary */}
      <AppointmentFiltersAndSummary
        filterStatus={filterStatus}
        setFilterStatus={setFilterStatus}
        selectedDoctor={selectedDoctor}
        setSelectedDoctor={setSelectedDoctor}
        patientSearch={patientSearch}
        setPatientSearch={setPatientSearch}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        appointments={filteredAppointments}
        doctorOptions={doctorOptions}
        onReset={handleReset}
        showPatientSearch={true}
        showDateFilters={true}
        totalLabel="Upcoming Total"
      />

      {/* Edit Mode */}
      {editMode && (
        <AppointmentEditForm
          editData={editData}
          handleEditChange={handleEditChange}
          handleSaveEdit={handleSaveEdit}
          editMode={editMode}
          onCancel={() => setEditMode(null)}
        />
      )}

      {/* View Modal */}
      {viewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-full sm:max-w-[80%] md:max-w-[700px] lg:max-w-screen-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-bold text-gray-700">Appointment Details</h2>
            </div>
            <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {viewAppointment && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">ID</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.id}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.patientName || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Doctor</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.doctorName || "Unknown"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {formatDate(viewAppointment.appointmentDate || viewAppointment.createdAt)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Serial</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.serialNumber || "--"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.mobileNumber || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {getStatusLabel(viewAppointment)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Weight</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.weight || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.age || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Blood Group</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.bloodGroup || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Consultation Fee</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.consultationFee || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.paymentMethod || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.reason || "N/A"}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <div className="w-full p-2 border rounded-md text-sm text-gray-700 bg-gray-50">
                      {viewAppointment.address || "N/A"}
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setViewModalOpen(false)}
                className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <AppointmentTable
        data={filteredAppointments}
        onEdit={handleEditStart}
        onDelete={(ids) => {
          setBulkDeleteIds(ids);
          if (ids.length > 0) {
            setShowBulkDeleteModal(true);
          }
        }}
        onPrint={initiatePrint}
        currentPage={currentPage}
        itemsPerPage={appointmentsPerPage}
        onPageChange={handlePageChange}
        formatDate={formatDate}
        handleApproveClick={handleApproveClick}
        handleCancelClick={handleCancelClick}
        getStatusLabel={getStatusLabel}
        toggleDropdown={toggleDropdown}
        activeDropdown={activeDropdown}
        dropdownTriggerRefs={dropdownTriggerRefs}
        handleView={handleView}
        setActiveDropdown={setActiveDropdown}
      />

      {/* Approval Modal */}
      <ConfirmActionModal
        isOpen={approvalModalVisible}
        onClose={() => {
          setApprovalModalVisible(false);
          setApprovalTargetId(null);
        }}
        onConfirm={confirmApproval}
        ids={approvalTargetId}
        entityName="appointment"
        title="Confirm Approval"
        message="Are you sure you want to approve this appointment?"
        confirmText="Approve"
        cancelText="Cancel"
      />
      <ConfirmActionModal
        isOpen={cancelModalVisible}
        onClose={() => {
          setCancelModalVisible(false);
          setCancelTargetId(null);
        }}
        onConfirm={confirmCancellation}
        ids={cancelTargetId}
        entityName="appointment"
        title="Confirm Cancellation"
        message="Are you sure you want to cancel this appointment?"
        confirmText="Cancel Appointment"
        cancelText="Keep Pending"
      />

      {/* Bulk Delete Modal */}
      <ConfirmActionModal
        isOpen={showBulkDeleteModal}
        onClose={() => {
          setShowBulkDeleteModal(false);
          setBulkDeleteIds([]);
        }}
        onConfirm={confirmBulkDelete}
        ids={bulkDeleteIds}
        entityName="appointment"
        title="Confirm Delete"
        message={`Are you sure you want to delete ${bulkDeleteIds.length} appointment(s)?`}
        confirmText={deleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
      />
    </div>
  );
};

export default UpComing;